import React from 'react'
import { observer } from 'mobx-react-lite'

import { endOfMonth } from 'date-fns/endOfMonth'
import { startOfMonth } from 'date-fns/startOfMonth'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'

import {
  ANALYTICS_EVENTS,
  Drawer,
  Loader,
  MiniWidgetContainer,
  useBudgetsStore,
  useCategoryStore,
  useEvent,
  useGlobalCopyStore,
  useGlobalUiStore,
  useWidgetContainerProvider,
  useWidgetLoadTimer,
} from '../common'
import { type BudgetsCopy, type MiniWidgetProps } from '../core'

import { AddBudgets } from './components/AddBudgets'
import { BubbleBudgets } from './components/BubbleBudgets'
import { getBudgetDifference } from '../core/utils/BudgetUtil'
import { formatCurrency } from '../core/utils/NumberFormatting'

interface BubbleBudgetsContentProps {
  budgetsCopy: BudgetsCopy
  difference: number
  handleAddClick: () => void
  shouldShowZeroState: boolean
}

const BubbleBudgetsContent: React.FC<BubbleBudgetsContentProps> = ({
  budgetsCopy,
  difference,
  handleAddClick,
  shouldShowZeroState,
}) => {
  const { availableHeight = 0 } = useWidgetContainerProvider()
  const initialHeightRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    if (availableHeight > 0 && initialHeightRef.current === null) {
      initialHeightRef.current = availableHeight
    }
  }, [availableHeight])

  const bubbleHeight = React.useMemo(() => {
    const baseHeight = initialHeightRef.current ?? availableHeight

    if (baseHeight > 0) return Math.round(Math.max(120, Math.min(450, baseHeight * 0.8))) - 40

    return 450
  }, [availableHeight])

  return (
    <Stack sx={{ height: '100%', justifyContent: 'space-between' }}>
      <BubbleBudgets
        createBudgetOnClick={handleAddClick}
        height={bubbleHeight}
        isMiniWidget={true}
        shouldShowZeroState={shouldShowZeroState}
      />

      {!shouldShowZeroState && (
        <Box ml={16}>
          <Text bold={true} color={difference < 0 ? 'error.main' : 'success.main'}>
            {formatCurrency(Math.abs(difference), '0,0')}
          </Text>
          <Text>
            {difference < 0 ? budgetsCopy.overspent_in_budgets : budgetsCopy.left_in_budgets}
          </Text>
        </Box>
      )}
    </Stack>
  )
}

const BubbleBudgetsMiniWidget = (props: MiniWidgetProps) => {
  const theme = useTheme()

  const { onEvent } = useEvent()
  const { detailedBudgets: budgets, isDataLoaded, loadBudgetData, setTheme } = useBudgetsStore()
  const { budgets: budgetsCopy, common: commonCopy } = useGlobalCopyStore()
  const { isInitialized, selectedAccounts } = useGlobalUiStore()
  const { loadDateRangeCategoryTotals, loadMonthlyCategoryTotals } = useCategoryStore()
  const difference = getBudgetDifference(budgets)

  const [isAddOpen, setIsAddOpen] = React.useState(false)

  React.useEffect(() => {
    if (!isDataLoaded) {
      loadBudgetData().finally()
    }
  }, [])

  React.useEffect(() => {
    setTheme(theme)
  }, [theme])

  React.useEffect(() => {
    if (isInitialized && !isDataLoaded) {
      loadDateRangeCategoryTotals(
        selectedAccounts,
        startOfMonth(new Date()),
        endOfMonth(new Date()),
      ).finally()
      loadMonthlyCategoryTotals(selectedAccounts).finally()
    }
  }, [isInitialized, selectedAccounts, isDataLoaded])

  const handleAddClick = () => {
    setIsAddOpen(true)
    onEvent(ANALYTICS_EVENTS.BUDGETS_CLICK_ADD)
  }

  const handleBudgetAdded = () => {
    setIsAddOpen(false)
  }

  const handleBudgetsRecalculated = () => setIsAddOpen(false)

  useWidgetLoadTimer({
    widgetName: 'BubbleBudgetsMiniWidget',
    isLoaded: isDataLoaded,
  })

  if (!isInitialized || !isDataLoaded) return <Loader />

  const shouldShowZeroState = budgets.length === 0

  const summaryAria = shouldShowZeroState
    ? undefined
    : `${formatCurrency(Math.abs(difference), '0,0')} ${
        difference < 0 ? budgetsCopy.overspent_in_budgets : budgetsCopy.left_in_budgets
      }`

  return (
    <MiniWidgetContainer
      contentStyles={{ height: 'calc(100% - 66px)', ':last-child': { pb: 0 } }}
      primaryCtaLabel={shouldShowZeroState ? budgetsCopy.get_started_cta : budgetsCopy.details_cta}
      primaryCtaLabelAria={budgetsCopy.details_cta_aria}
      summaryAria={summaryAria}
      sx={{ height: '100%' }}
      title={budgetsCopy.budgets_title}
      {...props}
    >
      <BubbleBudgetsContent
        budgetsCopy={budgetsCopy}
        difference={difference}
        handleAddClick={handleAddClick}
        shouldShowZeroState={shouldShowZeroState}
      />

      <Drawer
        ariaLabelClose={commonCopy.close_aria}
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title={budgetsCopy.add_new_title}
      >
        <AddBudgets
          onAddBudget={handleBudgetAdded}
          onRecalculateBudgets={handleBudgetsRecalculated}
        />
      </Drawer>
    </MiniWidgetContainer>
  )
}

export default observer(BubbleBudgetsMiniWidget)
